import React, { useCallback, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import {
  DarkMode,
  LightMode,
  AutoAwesome,
  Check,
  Palette,
} from '@mui/icons-material';
import { useThemeStore, PaletteKey } from '../../stores/themeStore';

interface ThemeOption {
  key: PaletteKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  previewColors: string[];
}

const THEME_OPTIONS: ThemeOption[] = [
  {
    key: 'editorial-neutral',
    label: 'Editorial Neutral',
    description: 'Taupe & cream on pure black',
    icon: <Palette />,
    previewColors: ['#000000', '#A6948A', '#FFFDD0'],
  },
  {
    key: 'modern-minimalist',
    label: 'Modern Minimalist',
    description: 'Clean light aesthetic',
    icon: <LightMode />,
    previewColors: ['#F2F0EA', '#000000', '#8A9A5B'],
  },
  {
    key: 'cyber-street',
    label: 'Cyber Street',
    description: 'Dark mode with neon accents',
    icon: <DarkMode />,
    previewColors: ['#0D0D0D', '#7EC8A0', '#5B8DEF'],
  },
  {
    key: 'dark-luxe',
    label: 'Dark Luxe',
    description: 'Premium violet & pink gradients',
    icon: <AutoAwesome />,
    previewColors: ['#0A0A0A', '#7C3AED', '#EC4899'],
  },
];

/**
 * ThemePicker — dropdown selector that lets users choose between
 * Cyber Street (dark), Modern Minimalist (light), and Dark Luxe themes.
 * Shows color preview swatches and a check mark on the active theme.
 */
const ThemePicker: React.FC<{ onSelect?: () => void }> = ({ onSelect }) => {
  const palette = useThemeStore((state) => state.palette);
  const setPalette = useThemeStore((state) => state.setPalette);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (key: PaletteKey) => {
      document.body.classList.add('theme-transitioning');
      setPalette(key);
      setAnchorEl(null);
      onSelect?.();
      setTimeout(() => {
        document.body.classList.remove('theme-transitioning');
      }, 200);
    },
    [setPalette, onSelect],
  );

  // Determine which icon to show on the trigger button based on active theme
  const activeOption = THEME_OPTIONS.find((o) => o.key === palette) || THEME_OPTIONS[0];

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Choose theme"
        aria-controls={open ? 'theme-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        color="inherit"
        size="medium"
      >
        {activeOption.icon}
      </IconButton>

      <Menu
        id="theme-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
              mt: 1,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            Theme
          </Typography>
        </Box>

        {THEME_OPTIONS.map((option) => {
          const isActive = palette === option.key;
          return (
            <MenuItem
              key={option.key}
              onClick={() => handleSelect(option.key)}
              selected={isActive}
              sx={{
                py: 1.5,
                px: 2,
                gap: 1.5,
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: isActive ? 'primary.main' : 'text.secondary' }}>
                {option.icon}
              </ListItemIcon>

              <ListItemText
                primary={option.label}
                secondary={option.description}
                primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 700 : 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />

              {/* Color preview swatches */}
              <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', alignItems: 'center' }}>
                {option.previewColors.map((color, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: color,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                ))}
                {isActive && (
                  <Check sx={{ fontSize: 18, ml: 0.5, color: 'primary.main' }} />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default ThemePicker;
